import { IonButton, IonSpinner, IonToast, useIonModal } from '@ionic/react';
import { IconLink, IconQuirktoken } from './QuirkIcons/index.js';
import { getBackendURL, useBackendDomain } from '../hooks/useBackendDomain.js';
import {
	useGetOwnLedgerBalanceQuery,
	useGetStripeSessionQuery,
	useLazyStartStripeSessionQuery
} from '../api/UserLedgerAPI.js';
import { Confirmation } from '../pages/Confirmation.js';
import { BuyTokenModal } from '../pages/admin/BuyTokenModal.js';
import { useEffect, useReducer } from 'react';
import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query/fetchBaseQuery.js';
import { useCurrentUser } from '../hooks/useCurrentUser.js';
import { usePlatform } from '../hooks/usePlatform.js';
import styles from './TokenPurchase.module.css';
import { useGetOwnProfileQuery } from '../api/UserApi.js';
import { produce } from 'immer';
import { TokenPurchaseReducer } from '../reducers/TokenPurchaseReducer.js';

export const TokenPurchase = () => {
	const [triggerStripe] = useLazyStartStripeSessionQuery();
	const stateUser = useCurrentUser();
	const { data: user } = useGetOwnProfileQuery(stateUser._id);
	const { data: { response: tokens } = { response: 0 } } = useGetOwnLedgerBalanceQuery(undefined, {
		refetchOnFocus: true
	});
	const platform = usePlatform();

	const [state, dispatch] = useReducer(produce(TokenPurchaseReducer), {
		platform: 'web',
		isLoading: false,
		isSuccess: false,
		isError: false,
		stripeData: null,
		stripeError: null,
		buyTokenAmountSubmit: 0,
		paymentsLabel: 'Buy Tokens',
		stripePaymentLaunched: false
	});

	const {
		isLoading,
		isSuccess,
		isError,
		stripeData,
		stripeError,
		buyTokenAmountSubmit,
		paymentsLabel,
		stripePaymentLaunched
	} = state;

	useEffect(() => {
		dispatch({ type: 'PLATFORM', payload: platform || 'web' });
	}, [platform]);

	const [domain] = useBackendDomain();
	const { data: stripeSession } = useGetStripeSessionQuery(stripeData?.stripeSession as string, {
		skip: !stripeData,
		refetchOnFocus: true
	});

	const addTokenAmount = async () => {
		dismissBuyTokens();
		if (user && buyTokenAmountSubmit) {
			dispatch({ type: 'LOADING', payload: true });

			try {
				const res = await triggerStripe({ type: 'token', quantity: buyTokenAmountSubmit });

				if (res.isError) {
					dispatch({
						type: 'ERROR',
						payload:
							((res.error as FetchBaseQueryError)?.data as string) ||
							'There was an error processing payment'
					});
				} else {
					dispatch({ type: 'DATA', payload: res.data });
				}
			} catch (e) {
				dispatch({
					type: 'ERROR',
					payload: 'There was an error processing payment'
				});
			}
		}
	};
	const [presentBuyTokens, dismissBuyTokens] = useIonModal(Confirmation, {
		onDismiss: () => dismissBuyTokens(),
		cancelAction: () => {
			dispatch({ type: 'PAYMENT_LAUNCHED', payload: true });
		},
		action: addTokenAmount,
		actionLabel: 'Buy Tokens',
		dismissLabel: 'Cancel',
		content: (
			<BuyTokenModal
				user={user}
				setBuyTokenAmountSubmit={(amount: number) => dispatch({ type: 'AMOUNT', payload: amount })}
			/>
		)
	});

	return (
		<div>
			<div>
				<div className={styles.jewelItem}>
					<div className="quirkToken">
						<IconQuirktoken />
					</div>
					<span data-testid="tokenAmount">{tokens}</span>
				</div>
			</div>
			<div>
				<div className={styles.stripeButton}>
					<IonButton
						color="primary"
						fill="outline"
						size="default"
						className="radius8"
						expand="block"
						data-testid="buyTokensButton"
						onClick={() => {
							if (platform !== 'web') {
								window.open(getBackendURL('main/settings/profile', domain), '_blank');
								return;
							}
							if (isSuccess && stripeSession?.url && !stripePaymentLaunched) {
								dispatch({ type: 'PAYMENT_LAUNCHED', payload: true });
								window.open(stripeSession?.url, '_blank');
							} else {
								dispatch({ type: 'PAYMENT_LAUNCHED', payload: false });
								presentBuyTokens();
							}
						}}
					>
						<IonSpinner hidden={!isLoading} name="crescent" />
						{!isLoading &&
							!stripePaymentLaunched &&
							stripeSession &&
							isSuccess &&
							stripeSession?.status !== 'complete' && <IconLink />}
						<div className="width10" />
						{paymentsLabel}
					</IonButton>
				</div>
			</div>
			<IonToast isOpen={isError} duration={1500} message={stripeError || ''} position="top" color="danger" />
		</div>
	);
};
